import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft,
  Compass,
  Flame,
  TrendingUp,
  Target,
  Smile,
  Meh,
  Frown,
  Heart,
  Angry,
  Trophy,
  Calendar,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const moodConfig = {
  great: { icon: Heart, color: "bg-green-500", label: "Great" },
  good: { icon: Smile, color: "bg-[#8B5CF6]", label: "Good" },
  okay: { icon: Meh, color: "bg-[#D4AF37]", label: "Okay" },
  low: { icon: Frown, color: "bg-orange-500", label: "Low" },
  difficult: { icon: Angry, color: "bg-red-500", label: "Difficult" },
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await api.get("/analytics/mood");
        setAnalytics(response.data);
      } catch (error) {
        console.error("Error fetching analytics:", error);
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const totalMoods = analytics ? Object.values(analytics.mood_distribution).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="analytics-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Compass className="w-4 h-4 text-[#070B14]" />
            </div>
            <span className="font-display text-xl text-white">Insights</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Title */}
        <div className="animate-fade-in-up">
          <h1 className="font-display text-3xl text-white mb-2">Your Journey Insights</h1>
          <p className="text-white/60 font-body">
            Track your progress and understand your patterns, {user?.first_name}
          </p>
        </div>

        {/* Streak Cards */}
        <div className="grid grid-cols-2 gap-4 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <div className="glass-card p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-400" />
              </div>
              <span className="text-white/70 font-body text-sm">Current Streak</span>
            </div>
            <p className="font-display text-4xl text-white">{analytics?.current_streak || 0}</p>
            <p className="text-white/50 text-sm font-body">days</p>
          </div>

          <div className="glass-card p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <span className="text-white/70 font-body text-sm">Longest Streak</span>
            </div>
            <p className="font-display text-4xl text-white">{analytics?.longest_streak || 0}</p>
            <p className="text-white/50 text-sm font-body">days</p>
          </div>
        </div>

        {/* Guidance Effectiveness */}
        <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <Target className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h3 className="font-display text-lg text-white">Guidance Effectiveness</h3>
              <p className="text-white/50 text-sm font-body">How often the guidance helped you</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-white/70 font-body">{analytics?.guidance_effectiveness || 0}%</span>
              <span className="text-white/50 text-sm font-body">
                {analytics?.total_reflections || 0} reflections
              </span>
            </div>
            <Progress 
              value={analytics?.guidance_effectiveness || 0} 
              className="h-3 bg-white/10"
            />
          </div>
        </div>

        {/* Mood Distribution */}
        <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-[#C4B5FD]" />
            </div>
            <div>
              <h3 className="font-display text-lg text-white">Mood Distribution</h3>
              <p className="text-white/50 text-sm font-body">Your emotional journey</p>
            </div>
          </div>

          {totalMoods === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-white/30 mx-auto mb-3" />
              <p className="text-white/50 font-body">No reflections yet</p>
              <p className="text-white/30 text-sm font-body">Start reflecting to see your mood patterns</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(moodConfig).map(([mood, config]) => {
                const count = analytics?.mood_distribution?.[mood] || 0;
                const percentage = totalMoods > 0 ? (count / totalMoods) * 100 : 0;
                const Icon = config.icon;

                return (
                  <div key={mood} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${
                          mood === 'great' ? 'text-green-400' :
                          mood === 'good' ? 'text-[#C4B5FD]' :
                          mood === 'okay' ? 'text-[#D4AF37]' :
                          mood === 'low' ? 'text-orange-400' :
                          'text-red-400'
                        }`} />
                        <span className="text-white/80 font-body text-sm capitalize">{config.label}</span>
                      </div>
                      <span className="text-white/50 text-sm font-body">{count} ({Math.round(percentage)}%)</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${config.color} rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Weekly Trend */}
        {analytics?.weekly_mood_trend && analytics.weekly_mood_trend.length > 0 && (
          <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-display text-lg text-white">This Week</h3>
                <p className="text-white/50 text-sm font-body">Your recent mood trend</p>
              </div>
            </div>

            <div className="flex justify-between items-end gap-2">
              {analytics.weekly_mood_trend.slice(0, 7).reverse().map((day, idx) => {
                const config = moodConfig[day.mood] || moodConfig.okay;
                const Icon = config.icon;
                const date = new Date(day.date);
                const dayName = date.toLocaleDateString("en-US", { weekday: "short" });

                return (
                  <div key={idx} className="flex flex-col items-center gap-2 flex-1">
                    <div className={`w-8 h-8 rounded-full ${
                      day.mood === 'great' ? 'bg-green-500/20' :
                      day.mood === 'good' ? 'bg-[#8B5CF6]/20' :
                      day.mood === 'okay' ? 'bg-[#D4AF37]/20' :
                      day.mood === 'low' ? 'bg-orange-500/20' :
                      'bg-red-500/20'
                    } flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${
                        day.mood === 'great' ? 'text-green-400' :
                        day.mood === 'good' ? 'text-[#C4B5FD]' :
                        day.mood === 'okay' ? 'text-[#D4AF37]' :
                        day.mood === 'low' ? 'text-orange-400' :
                        'text-red-400'
                      }`} />
                    </div>
                    <span className="text-xs text-white/50 font-body">{dayName}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Summary Card */}
        <div className="gold-accent-card p-6 animate-fade-in-up" style={{ animationDelay: "0.5s" }}>
          <h3 className="font-display text-xl text-white mb-3">Keep Going!</h3>
          <p className="text-white/70 font-body">
            {analytics?.total_reflections === 0
              ? "Start your reflection journey today. Each day of awareness brings you closer to your best self."
              : analytics?.current_streak >= 7
              ? `Amazing! You've been consistent for ${analytics.current_streak} days. Your dedication to self-reflection is inspiring.`
              : analytics?.guidance_effectiveness >= 70
              ? "The guidance is resonating well with you. Keep following your intuition!"
              : "Every reflection is a step toward greater self-awareness. Keep going!"}
          </p>
        </div>

        {/* Action Button */}
        <div className="pt-4 animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
          <Button
            onClick={() => navigate("/reflection")}
            className="btn-gold w-full text-lg py-6"
          >
            <Heart className="w-5 h-5 mr-2" />
            Add Today's Reflection
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsPage;
